import { useState, useCallback, useEffect } from 'react';
import { Save, Eye, EyeOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { settingsAPI } from '../../lib/api';
import { useSite } from '../../context/SiteContext';
import { toast } from 'sonner';

const SettingsManagement = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const { refreshSettings } = useSite();

  const fetchSettings = useCallback(async () => {
    try { const res = await settingsAPI.getAll(); setSettings(res.data); }
    catch (err) { console.error('Failed to fetch settings:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const SECRET_MASK = '••••••••';
  
  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out masked secret values before sending
      const secretKeys = ['pica_secret_key', 'pica_gmail_connection_key', 'smtp_password', 'vapid_private_key'];
      const filteredSettings = { ...settings };
      
      secretKeys.forEach(key => {
        if (filteredSettings[key] === SECRET_MASK) {
          delete filteredSettings[key];
        }
      });
      
      await settingsAPI.update(filteredSettings); 
      toast.success('সেটিংস সেভ হয়েছে এবং সাইটে আপডেট হয়েছে'); 
      setLastSaved(new Date().toLocaleTimeString('bn-BD'));
      fetchSettings(); 
      // Refresh site context to update all pages
      if (refreshSettings) {
        await refreshSettings();
      }
    }
    catch (err) { toast.error('সেভ করতে সমস্যা হয়েছে'); }
    setSaving(false);
  };

  const handleTestEmail = async () => {
    try {
      const result = await settingsAPI.testEmail();
      if (result.data.success) toast.success(`টেস্ট ইমেইল পাঠানো হয়েছে (${result.data.method})`);
      else toast.error('ইমেইল পাঠাতে সমস্যা হয়েছে');
    } catch (err) { toast.error('টেস্ট ইমেইল ব্যর্থ'); }
  };

  const toggleSecretVisibility = (key) => setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) return <div className="animate-pulse"><div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg" /></div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">সেটিংস</h1>
          {lastSaved && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
              <CheckCircle size={12} />
              সর্বশেষ সেভ: {lastSaved}
            </p>
          )}
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={fetchSettings} className="flex-1 sm:flex-initial">
            <RefreshCw size={16} className="mr-2" /> রিফ্রেশ
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#2E7D32] hover:bg-[#1B5E20] flex-1 sm:flex-initial">
            <Save size={16} className="mr-2" /> {saving ? 'সেভ হচ্ছে...' : 'সেভ করুন'}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6">
        <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center gap-2">
          <AlertCircle size={16} />
          সেটিংস পরিবর্তন করে সেভ করলে সাথে সাথে পুরো সাইটে আপডেট হবে।
        </p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="general">সাধারণ</TabsTrigger>
          <TabsTrigger value="email">ইমেইল</TabsTrigger>
          <TabsTrigger value="api">API Keys</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">সাইট নাম</label><Input value={settings.site_name || ''} onChange={(e) => setSettings({...settings, site_name: e.target.value})} data-testid="settings-site-name" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ট্যাগলাইন</label><Input value={settings.site_tagline || ''} onChange={(e) => setSettings({...settings, site_tagline: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logo URL</label><Input value={settings.logo_url || ''} onChange={(e) => setSettings({...settings, logo_url: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon URL</label><Input value={settings.icon_url || ''} onChange={(e) => setSettings({...settings, icon_url: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">যোগাযোগ ইমেইল</label><Input value={settings.contact_email || ''} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} data-testid="settings-contact-email" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">যোগাযোগ ফোন</label><Input value={settings.contact_phone || ''} onChange={(e) => setSettings({...settings, contact_phone: e.target.value})} data-testid="settings-contact-phone" /></div>
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ঠিকানা</label><Input value={settings.contact_address || ''} onChange={(e) => setSettings({...settings, contact_address: e.target.value})} /></div>
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">WhatsApp ডিফল্ট মেসেজ</label><Textarea value={settings.whatsapp_default_message || ''} onChange={(e) => setSettings({...settings, whatsapp_default_message: e.target.value})} rows={2} /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="email">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">PicaOS Gmail প্রাইমারি, SMTP ফলব্যাক</p>
              <Button variant="outline" size="sm" onClick={handleTestEmail}>টেস্ট ইমেইল</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Host</label><Input value={settings.smtp_host || ''} onChange={(e) => setSettings({...settings, smtp_host: e.target.value})} placeholder="smtp.gmail.com" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Port</label><Input type="number" value={settings.smtp_port || ''} onChange={(e) => setSettings({...settings, smtp_port: e.target.value})} placeholder="587" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP User</label><Input value={settings.smtp_user || ''} onChange={(e) => setSettings({...settings, smtp_user: e.target.value})} /></div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Password</label>
                <div className="relative">
                  <Input type={showSecrets.smtp_password ? 'text' : 'password'} value={settings.smtp_password || ''} onChange={(e) => setSettings({...settings, smtp_password: e.target.value})} />
                  <button type="button" onClick={() => toggleSecretVisibility('smtp_password')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showSecrets.smtp_password ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Email</label><Input value={settings.smtp_from_email || ''} onChange={(e) => setSettings({...settings, smtp_from_email: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Admin Notification Email</label><Input value={settings.admin_notification_email || ''} onChange={(e) => setSettings({...settings, admin_notification_email: e.target.value})} /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="api">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700 space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">API keys এনক্রিপ্টেড অবস্থায় সেভ হয়। সেভ করার পর আবার দেখা যাবে না।</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PicaOS Secret Key</label>
                <div className="relative">
                  <Input type={showSecrets.pica_secret ? 'text' : 'password'} value={settings.pica_secret_key || ''} onChange={(e) => setSettings({...settings, pica_secret_key: e.target.value})} placeholder="sk_test_..." />
                  <button type="button" onClick={() => toggleSecretVisibility('pica_secret')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showSecrets.pica_secret ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PicaOS Gmail Connection Key</label>
                <div className="relative">
                  <Input type={showSecrets.pica_gmail ? 'text' : 'password'} value={settings.pica_gmail_connection_key || ''} onChange={(e) => setSettings({...settings, pica_gmail_connection_key: e.target.value})} placeholder="test::gmail::..." />
                  <button type="button" onClick={() => toggleSecretVisibility('pica_gmail')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showSecrets.pica_gmail ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsManagement;
